import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ReviewDialog from "@/components/ReviewDialog";
import { Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

function PayDepositDialog({ quote, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(30);
  const [busy, setBusy] = useState(false);
  const total = (parseFloat(quote.quoted_price || 0) * (quote.quantity || 1)) || 0;
  const deposit = total * pct / 100;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/escrows", { quote_id: quote.id, deposit_percent: pct });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("No checkout URL received");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid={`pay-deposit-${quote.id}`}><ShieldCheck className="w-3.5 h-3.5 mr-1" />Pay deposit</Button></DialogTrigger>
      <DialogContent data-testid="pay-deposit-dialog">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Lock in your order with Trade Assurance</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Your deposit is held by Safeway Finland and released to the supplier only after your shipment is delivered.</p>
          <div className="p-4 rounded-md bg-muted/40 border border-border">
            <div className="text-xs overline text-muted-foreground">Order</div>
            <div className="mt-1 text-secondary font-medium">{quote.product_name}</div>
            <div className="text-xs text-muted-foreground">Qty {quote.quantity} × ${parseFloat(quote.quoted_price).toFixed(2)} = <b>${total.toFixed(2)}</b></div>
          </div>
          <div>
            <Label>Deposit percentage</Label>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {[20, 30, 50, 100].map((p) => (
                <button key={p} type="button" onClick={() => setPct(p)} data-testid={`pct-${p}`}
                  className={`h-10 rounded-md text-sm font-semibold border ${pct === p ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-secondary"}`}>
                  {p}%
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">You pay now</span>
              <span className="font-heading text-2xl font-black text-secondary">${deposit.toFixed(2)}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Test mode — use card <code className="font-mono">4242 4242 4242 4242</code>, any future expiry and CVC.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="pay-deposit-submit" onClick={submit} disabled={busy || total < 1}>{busy ? "Redirecting…" : `Pay $${deposit.toFixed(2)}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BuyerQuotes() {
  const [items, setItems] = useState([]);
  const [reviewable, setReviewable] = useState([]);
  const [escrows, setEscrows] = useState([]);

  const load = () => {
    api.get("/buyer/quotes").then((r) => setItems(r.data)).catch(() => {});
    api.get("/buyer/reviewable").then((r) => setReviewable(r.data)).catch(() => {});
    api.get("/buyer/escrows").then((r) => setEscrows(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const canReview = (q) => reviewable.some((r) => r.quote_id === q.id);
  const hasEscrow = (q) => escrows.some((e) => e.quote_id === q.id && ["pending", "held", "released"].includes(e.status));

  return (
    <DashboardLayout title="My quote requests" actions={<Link to="/buyer/catalog"><Button data-testid="browse-catalog-btn">Browse catalog</Button></Link>}>
      <Card>
        <CardHeader><CardTitle className="font-heading tracking-tight">Requests</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="quotes-empty">No quote requests yet.</div>
          ) : (
            <Table data-testid="quotes-table">
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Qty</TableHead><TableHead>Target</TableHead><TableHead>Destination</TableHead><TableHead>Quoted</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((q) => {
                  const info = reviewable.find((r) => r.quote_id === q.id);
                  const canPay = q.quoted_price && ["quoted", "closed"].includes(q.status) && !hasEscrow(q);
                  return (
                    <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                      <TableCell className="font-medium text-secondary"><Link to={`/buyer/products/${q.product_id}`} className="hover:text-primary">{q.product_name}</Link></TableCell>
                      <TableCell>{q.quantity}</TableCell>
                      <TableCell>{q.target_price ? `${q.target_price}` : "—"}</TableCell>
                      <TableCell>{q.destination_country || "—"}</TableCell>
                      <TableCell>{q.quoted_price ? <span className="font-semibold text-secondary">{q.quoted_price}</span> : "—"}</TableCell>
                      <TableCell><StatusBadge status={q.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        {canPay && <PayDepositDialog quote={q} onSubmitted={load} />}
                        {canReview(q) && info && (
                          <ReviewDialog
                            supplierId={info.supplier_id}
                            supplierName={info.supplier_name}
                            quoteId={q.id}
                            onSubmitted={load}
                            testid={`review-${q.id}`}
                            trigger={<Button size="sm" variant="outline" data-testid={`review-${q.id}-btn`}><Star className="w-3.5 h-3.5 mr-1" />Rate supplier</Button>}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
